import { type Message as AgUiMessage, type useAgent } from '@copilotkit/react-core/v2'
import { type CorpusResponse } from 'shared/api/copilot'
import {
  type CopilotConversationApi,
  type CopilotSendToolResultArgs,
  type JustSentImagePreview,
  type SendUserMessageOptions
} from 'shared/copilot/hooks/useCopilotAgentConversation'
import { type UseCopilotCsvAttachmentResult } from 'shared/copilot/hooks/useCopilotCsvAttachment'
import { type UseCopilotImageAttachmentsResult } from 'shared/copilot/hooks/useCopilotImageAttachments'
import { type useCopilotDatasourceSession } from 'shared/copilot/hooks/useCopilotDatasourceSession'
import { type useCopilotKnowledgeSourcesSession } from 'shared/copilot/hooks/useCopilotKnowledgeSourcesSession'

type DatasourceSession = ReturnType<typeof useCopilotDatasourceSession>
type KnowledgeSourcesSession = ReturnType<typeof useCopilotKnowledgeSourcesSession>

export type CopilotAgentConversationReturn = CopilotConversationApi & {
  agent: ReturnType<typeof useAgent>['agent']
  selectedDatasourceIds: string[]
  selectedEntityTypes: DatasourceSession['selectedEntityTypes']
  selectedCirrusSource: DatasourceSession['selectedCirrusSource']
  hasSelectedDatasource: boolean
  setSelectedDatasources: DatasourceSession['setSelectedDatasources']
  setSelectedCirrusSource: DatasourceSession['setSelectedCirrusSource']
  clearSelectedDatasource: DatasourceSession['clearSelectedDatasource']
  reconcileDatasourceWithAvailableOptions: DatasourceSession['reconcileWithAvailableOptions']
  selectedKnowledgeSourceIds: string[]
  hasSelectedKnowledgeSource: boolean
  setSelectedKnowledgeSources: (corpusIds: string[], corpora: CorpusResponse[]) => void
  clearSelectedKnowledgeSource: (corpusId?: string) => void
  reconcileKnowledgeSourcesWithAvailableCorpora: KnowledgeSourcesSession['reconcileWithAvailableCorpora']
  attachedCsvFile: UseCopilotCsvAttachmentResult['attachedFile']
  pendingCsvFilename: UseCopilotCsvAttachmentResult['pendingFilename']
  csvAttachmentStatus: UseCopilotCsvAttachmentResult['status']
  csvAttachmentErrorReason: UseCopilotCsvAttachmentResult['errorReason']
  isCsvUploading: boolean
  attachCsvFile: UseCopilotCsvAttachmentResult['attachFile']
  retryCsvAttachment: () => Promise<void>
  clearCsvAttachment: UseCopilotCsvAttachmentResult['cancelAttachment']
  dismissCsvAttachmentError: UseCopilotCsvAttachmentResult['clearAttachment']
  imageAttachments: UseCopilotImageAttachmentsResult['attachments']
  imageAttachmentErrorReason: UseCopilotImageAttachmentsResult['errorReason']
  isImageUploading: boolean
  attachImageFiles: UseCopilotImageAttachmentsResult['attachFiles']
  removeImageAttachment: UseCopilotImageAttachmentsResult['removeAttachment']
  retryImageAttachment: UseCopilotImageAttachmentsResult['retryAttachment']
  dismissImageAttachmentError: UseCopilotImageAttachmentsResult['dismissError']
  /**
   * Blob preview + real filename for an image the composer just sent, keyed
   * by its gcsUri — available the instant the message is added, well before
   * the thread's own signed-URL lookup could ever produce one for a brand
   * new URI. See `CopilotUserMessageAttachments` for how the thread uses
   * this as a stand-in until the real signed URL is ready.
   */
  justSentImagePreviewByGcsUri: Readonly<Record<string, JustSentImagePreview>>
}

type DatasourceConversationFields = Pick<
  CopilotAgentConversationReturn,
  | 'selectedDatasourceIds'
  | 'selectedEntityTypes'
  | 'selectedCirrusSource'
  | 'hasSelectedDatasource'
  | 'setSelectedDatasources'
  | 'setSelectedCirrusSource'
  | 'clearSelectedDatasource'
  | 'reconcileDatasourceWithAvailableOptions'
>

type KnowledgeSourceConversationFields = Pick<
  CopilotAgentConversationReturn,
  | 'selectedKnowledgeSourceIds'
  | 'hasSelectedKnowledgeSource'
  | 'setSelectedKnowledgeSources'
  | 'clearSelectedKnowledgeSource'
  | 'reconcileKnowledgeSourcesWithAvailableCorpora'
>

type CsvAttachmentConversationFields = Pick<
  CopilotAgentConversationReturn,
  | 'attachedCsvFile'
  | 'pendingCsvFilename'
  | 'csvAttachmentStatus'
  | 'csvAttachmentErrorReason'
  | 'isCsvUploading'
  | 'attachCsvFile'
  | 'retryCsvAttachment'
  | 'clearCsvAttachment'
  | 'dismissCsvAttachmentError'
>

type ImageAttachmentConversationFields = Pick<
  CopilotAgentConversationReturn,
  | 'imageAttachments'
  | 'imageAttachmentErrorReason'
  | 'isImageUploading'
  | 'attachImageFiles'
  | 'removeImageAttachment'
  | 'retryImageAttachment'
  | 'dismissImageAttachmentError'
  | 'justSentImagePreviewByGcsUri'
>

const NOOP = (): void => undefined
const NOOP_ATTACH_CSV = async (): Promise<void> => undefined
const NOOP_ASYNC = async (): Promise<void> => undefined

const DISABLED_DATASOURCE_FIELDS: DatasourceConversationFields = {
  selectedDatasourceIds: [],
  selectedEntityTypes: [],
  selectedCirrusSource: null,
  hasSelectedDatasource: false,
  setSelectedDatasources: NOOP,
  setSelectedCirrusSource: NOOP,
  clearSelectedDatasource: NOOP,
  reconcileDatasourceWithAvailableOptions: NOOP
}

const DISABLED_KNOWLEDGE_SOURCE_FIELDS: KnowledgeSourceConversationFields = {
  selectedKnowledgeSourceIds: [],
  hasSelectedKnowledgeSource: false,
  setSelectedKnowledgeSources: NOOP,
  clearSelectedKnowledgeSource: NOOP,
  reconcileKnowledgeSourcesWithAvailableCorpora: NOOP
}

const DISABLED_CSV_FIELDS: CsvAttachmentConversationFields = {
  attachedCsvFile: null,
  pendingCsvFilename: null,
  csvAttachmentStatus: 'idle',
  csvAttachmentErrorReason: null,
  isCsvUploading: false,
  attachCsvFile: NOOP_ATTACH_CSV,
  retryCsvAttachment: NOOP_ASYNC,
  clearCsvAttachment: NOOP,
  dismissCsvAttachmentError: NOOP
}

const EMPTY_JUST_SENT_IMAGE_PREVIEWS: Readonly<Record<string, JustSentImagePreview>> = {}

const DISABLED_IMAGE_FIELDS: ImageAttachmentConversationFields = {
  imageAttachments: [],
  imageAttachmentErrorReason: null,
  isImageUploading: false,
  attachImageFiles: NOOP_ASYNC,
  removeImageAttachment: NOOP,
  retryImageAttachment: NOOP_ASYNC,
  dismissImageAttachmentError: NOOP,
  justSentImagePreviewByGcsUri: EMPTY_JUST_SENT_IMAGE_PREVIEWS
}

const buildImageAttachmentConversationFields = (
  enabled: boolean,
  imageAttachments: UseCopilotImageAttachmentsResult,
  justSentImagePreviewByGcsUri: Readonly<Record<string, JustSentImagePreview>>
): ImageAttachmentConversationFields => {
  if (!enabled) {
    return DISABLED_IMAGE_FIELDS
  }

  return {
    imageAttachments: imageAttachments.attachments,
    imageAttachmentErrorReason: imageAttachments.errorReason,
    isImageUploading: imageAttachments.isUploading,
    attachImageFiles: imageAttachments.attachFiles,
    removeImageAttachment: imageAttachments.removeAttachment,
    retryImageAttachment: imageAttachments.retryAttachment,
    dismissImageAttachmentError: imageAttachments.dismissError,
    justSentImagePreviewByGcsUri
  }
}

const buildDatasourceConversationFields = (
  enabled: boolean,
  datasource: DatasourceSession
): DatasourceConversationFields => {
  if (!enabled) {
    return DISABLED_DATASOURCE_FIELDS
  }

  return {
    selectedDatasourceIds: datasource.selectedDatasourceIds,
    selectedEntityTypes: datasource.selectedEntityTypes,
    selectedCirrusSource: datasource.selectedCirrusSource,
    hasSelectedDatasource: datasource.hasSelectedDatasource,
    setSelectedDatasources: datasource.setSelectedDatasources,
    setSelectedCirrusSource: datasource.setSelectedCirrusSource,
    clearSelectedDatasource: datasource.clearSelectedDatasource,
    reconcileDatasourceWithAvailableOptions: datasource.reconcileWithAvailableOptions
  }
}

const buildKnowledgeSourceConversationFields = (
  enabled: boolean,
  knowledgeSources: KnowledgeSourcesSession
): KnowledgeSourceConversationFields => {
  if (!enabled) {
    return DISABLED_KNOWLEDGE_SOURCE_FIELDS
  }

  return {
    selectedKnowledgeSourceIds: knowledgeSources.selectedKnowledgeSourceIds,
    hasSelectedKnowledgeSource: knowledgeSources.hasSelectedKnowledgeSource,
    setSelectedKnowledgeSources: knowledgeSources.setSelectedKnowledgeSources,
    clearSelectedKnowledgeSource: knowledgeSources.clearSelectedKnowledgeSource,
    reconcileKnowledgeSourcesWithAvailableCorpora: knowledgeSources.reconcileWithAvailableCorpora
  }
}

const buildCsvAttachmentConversationFields = (
  enabled: boolean,
  csvAttachment: UseCopilotCsvAttachmentResult,
  hasSelectedDatasource: boolean
): CsvAttachmentConversationFields => {
  if (!enabled) {
    return DISABLED_CSV_FIELDS
  }

  return {
    attachedCsvFile: csvAttachment.attachedFile,
    pendingCsvFilename: csvAttachment.pendingFilename,
    csvAttachmentStatus: csvAttachment.status,
    csvAttachmentErrorReason: csvAttachment.errorReason,
    isCsvUploading: csvAttachment.isUploading,
    attachCsvFile: async (file: File) => {
      await csvAttachment.attachFile(file, hasSelectedDatasource)
    },
    retryCsvAttachment: async () => {
      await csvAttachment.retryAttachment(hasSelectedDatasource)
    },
    clearCsvAttachment: csvAttachment.cancelAttachment,
    dismissCsvAttachmentError: csvAttachment.clearAttachment
  }
}

interface BuildCopilotAgentConversationReturnArgs {
  enableDatasourceSession: boolean
  enableKnowledgeSourceSession: boolean
  enableCsvAttachment: boolean
  enableImageAttachment: boolean
  datasource: DatasourceSession
  knowledgeSources: KnowledgeSourcesSession
  csvAttachment: UseCopilotCsvAttachmentResult
  imageAttachments: UseCopilotImageAttachmentsResult
  justSentImagePreviewByGcsUri: Readonly<Record<string, JustSentImagePreview>>
  agent: ReturnType<typeof useAgent>['agent']
  messages: AgUiMessage[]
  sendUserMessage: (message: string, options?: SendUserMessageOptions) => boolean
  sendToolResult: (args: CopilotSendToolResultArgs) => void
  isLoading: boolean
  isHydratingChat: boolean
  chatLoadFailed: boolean
  retryChatLoad: () => void
  stopGeneration: () => void
  currentChatId: string | null
  createNewChat: (options?: { initialMessage?: string }) => void
  loadChat: (chatId: string) => void
}

export const buildCopilotAgentConversationReturn = (
  args: BuildCopilotAgentConversationReturnArgs
): CopilotAgentConversationReturn => ({
  agent: args.agent,
  messages: args.messages,
  sendUserMessage: args.sendUserMessage,
  sendToolResult: args.sendToolResult,
  isLoading: args.isLoading,
  isHydratingChat: args.isHydratingChat,
  chatLoadFailed: args.chatLoadFailed,
  retryChatLoad: args.retryChatLoad,
  stopGeneration: args.stopGeneration,
  currentChatId: args.currentChatId ?? null,
  createNewChat: args.createNewChat,
  loadChat: args.loadChat,
  ...buildDatasourceConversationFields(args.enableDatasourceSession, args.datasource),
  ...buildKnowledgeSourceConversationFields(args.enableKnowledgeSourceSession, args.knowledgeSources),
  ...buildCsvAttachmentConversationFields(
    args.enableCsvAttachment,
    args.csvAttachment,
    args.datasource.hasSelectedDatasource
  ),
  ...buildImageAttachmentConversationFields(
    args.enableImageAttachment,
    args.imageAttachments,
    args.justSentImagePreviewByGcsUri
  )
})
